import { memo, useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Plus, X, MoveVertical as MoreVertical, Pencil, Trash2, ChevronDown } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import ScrollArea from "../ui/shared/ScrollArea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import type { Database } from "@/lib/supabase";
import { getDisplayName, formatWhatsAppJid } from "@/utils/contactUtils";
import { useAssistants } from "@/hooks/useAssistants";
import { usePipelines } from "@/hooks/usePipelines";
import { useContactCampaigns } from "@/hooks/useContactCampaigns";
import { useContactManagement, type AssistantPauseUnit } from "@/hooks/useContactManagement";
import { formatPauseRemaining, getAutomationUiState } from "@/utils/formatAutomationPause";
import { useContactOpportunity, type OpportunityData, type WonOpportunityData } from "@/hooks/useContactOpportunity";
import { useTags } from "@/hooks/useTags";
import { useTeam } from "@/hooks/useTeam";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { TagSelectorModal } from "./TagSelectorModal";
import { LeadScheduledMessages } from "./LeadScheduledMessages";
import { LeadFollowUpSequences } from "./LeadFollowUpSequences";
import { WonOpportunityDialog } from "../crm/WonOpportunityDialog";
import { LossReasonDialog } from "../crm/LossReasonDialog";
import { formatDateInAccountTz } from "@/lib/accountTimezone";
import { useTranslation } from "react-i18next";

type AccountContactBase = Database['public']['Tables']['account_contacts']['Row']

type AccountContact = AccountContactBase & {
  assistant_id?: string | null;
  assistant_disabled_until?: string | null;
  assistant_is_active?: boolean | null;
}

interface LeadManagementPanelProps {
  selectedContact: AccountContact | null;
  onContactUpdate?: () => void;
}

export const LeadManagementPanel = ({ selectedContact, onContactUpdate }: LeadManagementPanelProps) => {
  const { t } = useTranslation("chat");
  const { t: tCrm } = useTranslation("crm");
  // Hooks
  const { user } = useAuth();
  const { assistants, loading: loadingAssistants } = useAssistants();
  const { pipelines, pipelineStages, loading: loadingPipelines } = usePipelines();
  const { campaignHistory, loading: loadingCampaignHistory } = useContactCampaigns(selectedContact?.id || null);
  const { tags: availableTags, fetchTags } = useTags();
  const { members, loading: loadingMembers } = useTeam();
  const contactManagement = useContactManagement();
  const opportunityManagement = useContactOpportunity();

  // Estados
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>("");
  const [selectedStageId, setSelectedStageId] = useState<string>("");
  const [contactTags, setContactTags] = useState<string[]>([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [remainingPauseLabel, setRemainingPauseLabel] = useState<string>("");
  const [pausePopoverOpen, setPausePopoverOpen] = useState(false);
  const [pauseAmount, setPauseAmount] = useState<string>("24");
  const [pauseUnit, setPauseUnit] = useState<AssistantPauseUnit>("hours");
  // O contato pode ter vários cards, inclusive no mesmo funil
  const [opportunities, setOpportunities] = useState<OpportunityData[]>([]);
  const [editingOpportunityId, setEditingOpportunityId] = useState<string | null>(null);
  const [isCreatingOpportunity, setIsCreatingOpportunity] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [selectedAttributionId, setSelectedAttributionId] = useState<string | null>(null);
  const [updatingAttribution, setUpdatingAttribution] = useState(false);
  const [chatStatus, setChatStatus] = useState<'aberto' | 'concluido'>('aberto');
  const [updatingChatStatus, setUpdatingChatStatus] = useState(false);
  const [wonDialogOpen, setWonDialogOpen] = useState(false);
  const [lossReasonDialogOpen, setLossReasonDialogOpen] = useState(false);
  // opportunityId nulo = o card ainda será criado e fechado em seguida
  const [pendingWonData, setPendingWonData] = useState<{ opportunityId: string | null; pipelineId: string; stageId: string } | null>(null);
  const [pendingLossData, setPendingLossData] = useState<{ opportunityId: string | null; pipelineId: string; stageId: string } | null>(null);

  // Carregar dados do contato selecionado
  useEffect(() => {
    if (selectedContact) {
      // Atualizar assistente - usar account_assistant_id (campo oficial)
      const assistantId = (selectedContact as any).account_assistant_id || selectedContact.assistant_id || null;
      setSelectedAssistantId(assistantId);

      // Atualizar attribution_id
      setSelectedAttributionId(selectedContact.attribution_id || null);

      // Atualizar status do chat
      const status = (selectedContact as any).status_chat || 'aberto';
      setChatStatus(status);

      // Atualizar tags
      setContactTags(selectedContact.tags || []);

      // Carregar oportunidade existente
      loadOpportunity();
    }
  }, [selectedContact]);

  // Tempo restante da pausa (texto amigável) e reativação automática no banco ao expirar
  useEffect(() => {
    if (!selectedContact?.assistant_disabled_until) {
      setRemainingPauseLabel("");
      return;
    }

    const contactId = selectedContact.id;
    let alreadyReactivated = false;

    const reactivate = () => {
      if (alreadyReactivated) return;
      alreadyReactivated = true;
      void contactManagement.enableAssistant(contactId).then((ok) => {
        if (ok) onContactUpdate?.();
      });
    };

    const tick = () => {
      const disabledUntil = new Date(selectedContact.assistant_disabled_until!);
      const diff = disabledUntil.getTime() - Date.now();

      if (diff <= 0) {
        setRemainingPauseLabel("");
        // Fast-path local: se a pausa já venceu e a flag ainda está em false,
        // reativamos no banco imediatamente (independente do pg_cron).
        if (selectedContact.assistant_is_active === false) {
          reactivate();
        }
        return;
      }

      setRemainingPauseLabel(formatPauseRemaining(diff));
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
    // enableAssistant is stable enough for our purposes; avoid re-subscribing on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedContact?.assistant_disabled_until,
    selectedContact?.assistant_is_active,
    selectedContact?.id,
    onContactUpdate,
  ]);

  // Carregar as oportunidades do contato
  const loadOpportunity = async () => {
    if (!selectedContact) return;

    const list = await opportunityManagement.getContactOpportunities(selectedContact.id);
    setOpportunities(list);
    setEditingOpportunityId(null);
    setIsCreatingOpportunity(false);
    setSelectedPipelineId("");
    setSelectedStageId("");
  };

  // Filtrar etapas do funil selecionado
  const filteredStages = useMemo(() => {
    if (!selectedPipelineId) return [];
    return pipelineStages.filter(stage => stage.pipeline_id === selectedPipelineId);
  }, [selectedPipelineId, pipelineStages]);

  // O formulário substitui a lista quando o contato ainda não tem card, quando
  // está criando um novo, ou quando está editando um card específico
  const showOpportunityForm =
    isCreatingOpportunity || editingOpportunityId !== null || opportunities.length === 0;

  // Cards que o contato já tem no funil escolhido. Só avisa ao criar um novo —
  // ao editar, o próprio card em edição estaria na lista.
  const duplicatesInSelectedPipeline = useMemo(() => {
    if (!isCreatingOpportunity || !selectedPipelineId) return [];
    return opportunities.filter(opp => opp.pipeline_id === selectedPipelineId);
  }, [isCreatingOpportunity, selectedPipelineId, opportunities]);

  const automationState = useMemo(() => {
    if (!selectedContact) return "desligada" as const;
    return getAutomationUiState({
      assistant_is_active: selectedContact.assistant_is_active,
      assistant_disabled_until: selectedContact.assistant_disabled_until ?? null,
    });
  }, [selectedContact]);

  // Extrair nomes únicos de campanhas
  const uniqueCampaignNames = useMemo(() => {
    if (!campaignHistory || campaignHistory.length === 0) return [];

    const names = campaignHistory
      .map(h => h.campaign_name)
      .filter((name): name is string => name !== null && name !== undefined && name.trim() !== '');

    const uniqueNames = [...new Set(names)];

    return uniqueNames.sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [campaignHistory]);

  // Handlers
  const handleAssistantChange = async (assistantId: string) => {
    if (!selectedContact) return;

    const success = await contactManagement.assignAssistant(selectedContact.id, assistantId === "none" ? null : assistantId);
    if (success) {
      setSelectedAssistantId(assistantId === "none" ? null : assistantId);
      onContactUpdate?.();
    }
  };

  const handleAssistantToggle = async (checked: boolean) => {
    if (!selectedContact) return;

    if (checked) {
      const success = await contactManagement.enableAssistant(selectedContact.id);
      if (success) onContactUpdate?.();
    } else {
      const success = await contactManagement.disableAssistant(selectedContact.id);
      if (success) onContactUpdate?.();
    }
  };

  const handlePauseForDuration = async () => {
    if (!selectedContact) return;
    const n = Number.parseInt(pauseAmount, 10);
    if (!Number.isFinite(n) || n < 1) return;

    const ok = await contactManagement.disableAssistantTemporarily(selectedContact.id, n, pauseUnit);
    if (ok) {
      setPausePopoverOpen(false);
      onContactUpdate?.();
    }
  };

  const handleStopIndefinitelyFromPopover = async () => {
    if (!selectedContact) return;
    const ok = await contactManagement.disableAssistant(selectedContact.id);
    if (ok) {
      setPausePopoverOpen(false);
      onContactUpdate?.();
    }
  };

  const handleTurnAutomationOnFromCard = async () => {
    if (!selectedContact) return;
    const ok = await contactManagement.enableAssistant(selectedContact.id);
    if (ok) onContactUpdate?.();
  };

  const handleAttributionChange = async (userId: string) => {
    if (!selectedContact || !user || !supabase) return;

    const canAssignOthers =
      user.can_assign_contacts === true || user.type === 'agency_user';
    const attributionValue = userId === 'none' ? null : userId;
    if (
      !canAssignOthers &&
      attributionValue &&
      attributionValue !== user.auth_user_id
    ) {
      return;
    }

    setUpdatingAttribution(true);

    try {

      const { error } = await supabase
        .from('account_contacts')
        .update({ attribution_id: attributionValue })
        .eq('id', selectedContact.id)
        .eq('account_id', user.account_id);

      if (error) throw error;

      setSelectedAttributionId(attributionValue);
      onContactUpdate?.();
      console.log('✅ [LeadManagementPanel] Atribuição atualizada com sucesso');
    } catch (error) {
      console.error('❌ [LeadManagementPanel] Erro ao atualizar atribuição:', error);
    } finally {
      setUpdatingAttribution(false);
    }
  };

  const handleChatStatusChange = async (status: 'aberto' | 'concluido') => {
    if (!selectedContact) return;

    setUpdatingChatStatus(true);

    const success = await contactManagement.updateChatStatus(selectedContact.id, status);

    if (success) {
      setChatStatus(status);
      onContactUpdate?.();
      console.log('✅ [LeadManagementPanel] Status do chat atualizado com sucesso');
    }

    setUpdatingChatStatus(false);
  };

  const handlePipelineChange = async (pipelineId: string) => {
    setSelectedPipelineId(pipelineId);
    setSelectedStageId(""); // Resetar etapa ao mudar funil
  };

  const handleStageChange = (stageId: string) => {
    setSelectedStageId(stageId);
  };

  const handleSaveOpportunity = async () => {
    if (!selectedContact || !selectedPipelineId || !selectedStageId) return;

    const selectedStage = filteredStages.find(s => s.id === selectedStageId);
    const stageType = selectedStage?.stage_type || 'normal';

    if (stageType === 'won') {
      setPendingWonData({ opportunityId: editingOpportunityId, pipelineId: selectedPipelineId, stageId: selectedStageId });
      setWonDialogOpen(true);
      return;
    }

    if (stageType === 'lost') {
      setPendingLossData({ opportunityId: editingOpportunityId, pipelineId: selectedPipelineId, stageId: selectedStageId });
      setLossReasonDialogOpen(true);
      return;
    }

    const success = editingOpportunityId
      ? await opportunityManagement.updateOpportunityStage(
          editingOpportunityId,
          selectedContact.id,
          selectedPipelineId,
          selectedStageId
        )
      : Boolean(await opportunityManagement.createOpportunity(
          selectedContact.id,
          selectedPipelineId,
          selectedStageId
        ));

    if (success) {
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
      await loadOpportunity();
      onContactUpdate?.();
    }
  };

  const handleRemoveOpportunity = async (opportunityId: string) => {
    const success = await opportunityManagement.deleteOpportunity(opportunityId);
    if (success) {
      await loadOpportunity();
      onContactUpdate?.();
    }
  };

  const handleEditOpportunity = (opportunity: OpportunityData) => {
    setEditingOpportunityId(opportunity.id);
    setIsCreatingOpportunity(false);
    setSelectedPipelineId(opportunity.pipeline_id);
    setSelectedStageId(opportunity.stage_id);
  };

  const handleNewOpportunity = () => {
    setEditingOpportunityId(null);
    setIsCreatingOpportunity(true);
    setSelectedPipelineId("");
    setSelectedStageId("");
  };

  const handleCancelEdit = () => {
    setEditingOpportunityId(null);
    setIsCreatingOpportunity(false);
    setSelectedPipelineId("");
    setSelectedStageId("");
  };

  /**
   * Ganho/perda precisam de um card existente. Quando o usuário manda um card
   * novo direto para uma etapa de ganho/perda, criamos antes de fechar.
   */
  const resolveOpportunityToClose = async (
    pending: { opportunityId: string | null; pipelineId: string; stageId: string }
  ): Promise<string | null> => {
    if (pending.opportunityId) return pending.opportunityId;
    if (!selectedContact) return null;
    return opportunityManagement.createOpportunity(
      selectedContact.id,
      pending.pipelineId,
      pending.stageId
    );
  };

  const handleConfirmWon = async (data: WonOpportunityData) => {
    if (!selectedContact || !pendingWonData) return;

    const opportunityId = await resolveOpportunityToClose(pendingWonData);

    const success = opportunityId
      ? await opportunityManagement.closeOpportunity(
          opportunityId,
          selectedContact.id,
          pendingWonData.stageId,
          'won',
          undefined,
          data
        )
      : false;

    if (success) {
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
      await loadOpportunity();
      onContactUpdate?.();
    }

    setWonDialogOpen(false);
    setPendingWonData(null);
  };

  const handleCancelWon = () => {
    setWonDialogOpen(false);
    setPendingWonData(null);
  };

  const handleConfirmLoss = async (lossReasonId: string, lostByUserId?: string) => {
    if (!selectedContact || !pendingLossData) return;

    const opportunityId = await resolveOpportunityToClose(pendingLossData);

    const success = opportunityId
      ? await opportunityManagement.closeOpportunity(
          opportunityId,
          selectedContact.id,
          pendingLossData.stageId,
          'lost',
          lossReasonId,
          undefined,
          lostByUserId
        )
      : false;

    if (success) {
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
      await loadOpportunity();
      onContactUpdate?.();
    }

    setLossReasonDialogOpen(false);
    setPendingLossData(null);
  };

  const handleCancelLoss = () => {
    setLossReasonDialogOpen(false);
    setPendingLossData(null);
  };

  const handleApplyTags = async (newTags: string[]) => {
    if (!selectedContact) return;

    const success = await contactManagement.setTags(selectedContact.id, newTags);

    if (success) {
      setContactTags(newTags);
      onContactUpdate?.();
    }
  };

  const handleTagCreated = () => {
    fetchTags();
  };

  const handleRemoveTag = async (tagName: string) => {
    if (!selectedContact) return;

    const success = await contactManagement.removeTag(selectedContact.id, tagName);

    if (success) {
      setContactTags(contactTags.filter(t => t !== tagName));
      onContactUpdate?.();
    }
  };

  if (!selectedContact) {
    return (
      <div className="flex-col gap-4 text-foreground dark:text-neutral-50 text-sm w-full mt-10 lg:mt-0 lg:max-w-[281px] h-full hidden lg:flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-card-muted dark:bg-neutral-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-muted-foreground dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground dark:text-neutral-50 mb-2">{t("panel.pickContact")}</h3>
          <p className="text-muted-foreground dark:text-neutral-300 text-sm">
            {t("panel.pickContactHint")}
          </p>
        </div>
      </div>
    );
  }

  const displayName = getDisplayName(selectedContact);
  const formattedPhone = selectedContact.phone || formatWhatsAppJid(selectedContact.jid);
  const contactEmail = selectedContact.email || 'Email não disponível';
  const createdDate = selectedContact.created_at
    ? formatDateInAccountTz(selectedContact.created_at)
    : 'Data não disponível';

  // Buscar membro atribuído (só com ID válido — evita match em auth_user_id null)
  const attributedMember = selectedAttributionId
    ? members.find(m => m.auth_user_id === selectedAttributionId)
    : undefined;
  const attributedUserName = attributedMember?.name || attributedMember?.email || null;

  return (
    <ScrollArea
      classNames={{
        root: "flex-col gap-4 text-foreground dark:text-neutral-50 text-sm w-full mt-10 lg:mt-0 lg:max-w-[281px] h-full hidden lg:flex",
      }}
    >
      <Accordion type="single" collapsible defaultValue="item-1">
        <AccordionItem value="item-1">
          <AccordionTrigger>{t("panel.contactData")}</AccordionTrigger>
          <AccordionContent>
            {" "}
            <div className=" flex flex-col gap-4">
              <div className="     text-primary dark:text-neutral-50 flex justify-between items-center">
                <Link to={`/crm/${selectedContact.id}`} className=" flex gap-2 items-center hover:text-primary/80 dark:hover:text-primary dark:text-blue-400 transition-colors duration-200">
                  <h2 className="  font-normal  ">{displayName}</h2>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M8.91667 3.66667L8.09417 4.48917L9.01583 5.41667H4.25V6.58333H9.01583L8.09417 7.505L8.91667 8.33333L11.25 6L8.91667 3.66667ZM1.91667 1.91667H6V0.75H1.91667C1.275 0.75 0.75 1.275 0.75 1.91667V10.0833C0.75 10.725 1.275 11.25 1.91667 11.25H6V10.0833H1.91667V1.91667Z"
                      fill="currentColor"
                    />
                  </svg>
                </Link>
              </div>

              <div className="flex items-center gap-2 text-[#0F172A] dark:text-[#FAFAFA]">
                <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 0.75H2C0.965 0.75 0.125 1.59 0.125 2.625V15.375C0.125 16.41 0.965 17.25 2 17.25H8C9.035 17.25 9.875 16.41 9.875 15.375V2.625C9.875 1.59 9.035 0.75 8 0.75ZM5 16.5C4.3775 16.5 3.875 15.9975 3.875 15.375C3.875 14.7525 4.3775 14.25 5 14.25C5.6225 14.25 6.125 14.7525 6.125 15.375C6.125 15.9975 5.6225 16.5 5 16.5ZM8.375 13.5H1.625V3H8.375V13.5Z"
                    fill="currentColor"
                  />
                </svg>

                <p>{formattedPhone}</p>
              </div>
              <div className="flex items-center gap-2 text-[#0F172A] dark:text-[#FAFAFA]">
                <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M14 0H2C1.175 0 0.5 0.675 0.5 1.5V10.5C0.5 11.325 1.175 12 2 12H14C14.825 12 15.5 11.325 15.5 10.5V1.5C15.5 0.675 14.825 0 14 0ZM13.7 3.1875L8.795 6.255C8.3075 6.5625 7.6925 6.5625 7.205 6.255L2.3 3.1875C2.1125 3.0675 2 2.865 2 2.6475C2 2.145 2.5475 1.845 2.975 2.1075L8 5.25L13.025 2.1075C13.4525 1.845 14 2.145 14 2.6475C14 2.865 13.8875 3.0675 13.7 3.1875Z"
                    fill="currentColor"
                  />
                </svg>

                <p>{contactEmail}</p>
              </div>
              <div className="flex items-center gap-2 text-[#0F172A] dark:text-[#FAFAFA]">
                <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10 8.75H7.75C7.3375 8.75 7 9.0875 7 9.5V11.75C7 12.1625 7.3375 12.5 7.75 12.5H10C10.4125 12.5 10.75 12.1625 10.75 11.75V9.5C10.75 9.0875 10.4125 8.75 10 8.75ZM10 1.25V2H4V1.25C4 0.8375 3.6625 0.5 3.25 0.5C2.8375 0.5 2.5 0.8375 2.5 1.25V2H1.75C0.9175 2 0.2575 2.675 0.2575 3.5L0.25 14C0.25 14.825 0.9175 15.5 1.75 15.5H12.25C13.075 15.5 13.75 14.825 13.75 14V3.5C13.75 2.675 13.075 2 12.25 2H11.5V1.25C11.5 0.8375 11.1625 0.5 10.75 0.5C10.3375 0.5 10 0.8375 10 1.25ZM11.5 14H2.5C2.0875 14 1.75 13.6625 1.75 13.25V5.75H12.25V13.25C12.25 13.6625 11.9125 14 11.5 14Z"
                    fill="currentColor"
                  />
                </svg>

                <p>Criado em {createdDate}</p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Seção de Atendimento removida conforme solicitado */}
      <Accordion type="single" collapsible defaultValue="item-2">
        <AccordionItem value="item-2">
          <AccordionTrigger>{t("common.assistant")}</AccordionTrigger>
          <AccordionContent>
            {loadingAssistants ? (
              <div className="flex flex-col gap-4 animate-pulse">
                <div className="flex gap-2 items-center">
                  <div className="h-10 bg-card-muted dark:bg-neutral-800/50 rounded-md flex-1"></div>
                  <div className="h-6 w-11 bg-card-muted dark:bg-neutral-800/50 rounded-full"></div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex gap-2 items-center">
                  <Select
                    value={selectedAssistantId || "none"}
                    onValueChange={handleAssistantChange}
                    disabled={loadingAssistants}
                  >
                    <SelectTrigger className="w-full min-w-0 overflow-hidden">
                      <SelectValue placeholder={t("common.pickAssistant")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="none">{t("common.noAssistant")}</SelectItem>
                        {assistants.filter(a => a.is_active).map((assistant) => (
                          <SelectItem key={assistant.id} value={assistant.id}>
                            {assistant.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Switch
                    checked={automationState !== "desligada"}
                    onCheckedChange={handleAssistantToggle}
                    disabled={!selectedAssistantId}
                    className="shrink-0"
                  />
                </div>

                <div className="rounded-[12px] border border-border bg-card dark:border-[#3F3F464D] dark:bg-[#18181B] p-3 flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-foreground dark:text-neutral-200">
                    <span>{t("panel.automationsAre")}</span>
                    {automationState === "ligada" && (
                      <Badge className="border-0 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-600 dark:text-white dark:hover:bg-emerald-600">{t("panel.stateOn")}</Badge>
                    )}
                    {automationState === "pausada" && (
                      <Badge className="border-0 bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-600 dark:text-white dark:hover:bg-red-600">{t("panel.statePaused")}</Badge>
                    )}
                    {automationState === "desligada" && (
                      <Badge className="border-0 bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-600 dark:text-white dark:hover:bg-red-600">{t("panel.stateOff")}</Badge>
                    )}
                  </div>

                  {automationState === "ligada" && (
                    <Popover open={pausePopoverOpen} onOpenChange={setPausePopoverOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="text-sm text-primary dark:text-blue-400 hover:text-primary/80 dark:text-blue-300 flex items-center gap-1 w-fit text-left"
                        >
                          {t("panel.pauseFor")} <ChevronDown className="w-4 h-4 shrink-0" aria-hidden />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[min(calc(100vw-2rem),20rem)] p-3 border border-border bg-card rounded-xl shadow-md dark:border-[#3F3F46] dark:!bg-[#18181B] dark:rounded-[7px] dark:shadow-none z-[100] overflow-visible"
                        align="start"
                        onInteractOutside={(e) => {
                          const el = e.target as HTMLElement | null;
                          if (
                            el?.closest?.('[data-slot="select-content"]') ||
                            el?.closest?.("[data-radix-select-content]")
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onFocusOutside={(e) => {
                          const el = e.target as HTMLElement | null;
                          if (
                            el?.closest?.('[data-slot="select-content"]') ||
                            el?.closest?.("[data-radix-select-content]")
                          ) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <div className="flex flex-col gap-3">
                          <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-2">
                            <Input
                              type="number"
                              min={1}
                              value={pauseAmount}
                              onChange={(e) => setPauseAmount(e.target.value)}
                              className="h-11 min-h-11 bg-white border-border text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-neutral-50 w-full min-w-0"
                              aria-label={t("panel.quantity")}
                            />
                            <Select value={pauseUnit} onValueChange={(v) => setPauseUnit(v as AssistantPauseUnit)}>
                              <SelectTrigger className="h-11 min-h-11 w-full min-w-0 bg-white border-border text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-neutral-50 justify-between">
                                <SelectValue placeholder={t("panel.unit")} />
                              </SelectTrigger>
                              <SelectContent
                                className="z-[300] bg-white border-border dark:bg-[#18181B] dark:border-[#3F3F46]"
                                position="popper"
                                sideOffset={6}
                              >
                                <SelectGroup>
                                  <SelectItem value="minutes" className="py-2.5 cursor-pointer">
                                    {t("panel.minutes")}
                                  </SelectItem>
                                  <SelectItem value="hours" className="py-2.5 cursor-pointer">
                                    {t("panel.hours")}
                                  </SelectItem>
                                  <SelectItem value="days" className="py-2.5 cursor-pointer">
                                    {t("panel.days")}
                                  </SelectItem>
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full bg-white text-[#0F172A] border-border hover:bg-[#F1F5F9] dark:bg-[#27272A] dark:text-neutral-50 dark:border-[#3F3F46] dark:hover:bg-[#1f1f23]"
                            onClick={handlePauseForDuration}
                            disabled={contactManagement.updating}
                          >
                            {t("panel.pauseForTime")}
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            className="w-full dark:bg-red-600 dark:hover:bg-red-500 dark:text-white"
                            onClick={handleStopIndefinitelyFromPopover}
                            disabled={contactManagement.updating}
                          >
                            {t("panel.stopIndefinitely")}
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}

                  {automationState === "pausada" && remainingPauseLabel && (
                    <>
                      <p className="text-sm font-medium text-primary dark:text-blue-400">
                        Pausada por {remainingPauseLabel}
                      </p>
                      <button
                        type="button"
                        onClick={handleTurnAutomationOnFromCard}
                        className="text-xs text-muted-foreground dark:text-neutral-400 hover:text-primary dark:text-neutral-200 underline w-fit transition-colors"
                        disabled={contactManagement.updating}
                      >
                        {t("panel.resumeNow")}
                      </button>
                    </>
                  )}

                  {automationState === "desligada" && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="w-full border-primary text-primary hover:bg-[#EEF4FF] hover:text-primary dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-400/10"
                      onClick={handleTurnAutomationOnFromCard}
                      disabled={contactManagement.updating || !selectedAssistantId}
                    >
                      {t("panel.turnOn")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion type="single" collapsible defaultValue="item-atendimento">
        <AccordionItem value="item-atendimento">
          <AccordionTrigger>{t("panel.support")}</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-6">
              {/* Usuário Atribuído */}
              <div className="flex flex-col gap-3">
                <p className="text-xs font-medium text-primary dark:text-neutral-400 uppercase tracking-wide">{t("panel.assignedUser")}</p>
                {loadingMembers ? (
                  <div className="flex flex-col gap-2 animate-pulse">
                    <div className="h-10 bg-card-muted dark:bg-neutral-800/50 rounded-md"></div>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const canAssignOthers =
                        user?.can_assign_contacts === true || user?.type === 'agency_user';
                      const lockedToOther =
                        !canAssignOthers &&
                        !!selectedContact?.attribution_id &&
                        selectedContact.attribution_id !== user?.auth_user_id;
                      return (
                        <>
                          <Select
                            value={selectedAttributionId || "none"}
                            onValueChange={handleAttributionChange}
                            disabled={updatingAttribution || lockedToOther}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t("panel.pickUser")} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem value="none">{t("panel.noUser")}</SelectItem>
                                {members
                                  .filter(m => m.is_active && m.auth_user_id)
                                  .filter(m =>
                                    canAssignOthers
                                      ? true
                                      : m.auth_user_id === user?.auth_user_id
                                  )
                                  .map((member) => (
                                    <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                                      {member.name || member.email}
                                    </SelectItem>
                                  ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                          {lockedToOther && (
                            <p className="text-xs text-amber-400/90">
                              {t("panel.assignedElsewhere")}
                            </p>
                          )}
                        </>
                      );
                    })()}
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${attributedUserName ? 'bg-green-500' : 'bg-neutral-500'}`} />
                      <span className={`text-xs font-medium ${attributedUserName ? 'text-primary dark:text-green-400' : 'text-muted-foreground dark:text-neutral-400'}`}>
                        {attributedUserName ? t('panel.assignedTo') : t('panel.unassigned')}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Separador */}
              <div className="h-px bg-border dark:bg-neutral-700/50" />

              {/* Status do Chat */}
              <div className="flex flex-col gap-3">
                <p className="text-xs font-medium text-primary dark:text-neutral-400 uppercase tracking-wide">{t("panel.chatStatus")}</p>
                <Select
                  value={chatStatus}
                  onValueChange={handleChatStatusChange}
                  disabled={updatingChatStatus}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("panel.pickStatus")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="aberto">{t("panel.open")}</SelectItem>
                      <SelectItem value="concluido">{t("panel.closed")}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${chatStatus === 'aberto' ? 'bg-green-500' : 'bg-blue-500'}`} />
                  <span className={`text-xs font-medium ${chatStatus === 'aberto' ? 'text-primary dark:text-green-400' : 'text-primary dark:text-blue-400'}`}>
                    {chatStatus === 'aberto' ? 'Chat Aberto' : 'Chat Concluído'}
                  </span>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion type="single" collapsible defaultValue="item-3">
        <AccordionItem value="item-3">
          <AccordionTrigger>{t("panel.crm")}</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-4">
              {!showOpportunityForm ? (
                <>
                  {opportunities.map((opportunity) => (
                <div key={opportunity.id} className="relative bg-card-muted dark:bg-neutral-800/50 border border-border dark:border-neutral-700/50 rounded-lg p-3 hover:border-primary/30 dark:hover:border-neutral-600/70 transition-all duration-200 group">
                  {/* Pipeline e Etapa */}
                  <div className="flex flex-col gap-2 pr-8">
                    {/* Pipeline */}
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        <span className="text-[10px] text-muted-foreground dark:text-neutral-400 uppercase tracking-wide font-medium">
                          {t("panel.pipeline")}
                        </span>
                        <span
                          className="text-sm font-semibold text-primary dark:text-white truncate"
                          title={opportunity.pipeline_name}
                        >
                          {opportunity.pipeline_name}
                        </span>
                      </div>
                    </div>

                    {/* Etapa */}
                    <div className="flex items-center gap-2 pl-4">
                      <svg className="w-3 h-3 text-primary/60 dark:text-neutral-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        <span className="text-[10px] text-muted-foreground dark:text-neutral-400 uppercase tracking-wide font-medium">
                          {t("panel.stage")}
                        </span>
                        <span
                          className="text-sm text-primary dark:text-neutral-200 truncate"
                          title={opportunity.stage_name}
                        >
                          {opportunity.stage_name}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Menu de Ações */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="absolute top-2 right-2 p-1.5 hover:bg-hover dark:hover:bg-border dark:bg-neutral-700/50 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                        aria-label={t("panel.options")}
                      >
                        <MoreVertical className="w-4 h-4 text-muted-foreground dark:text-neutral-400" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-1" align="end">
                      <button
                        onClick={() => handleEditOpportunity(opportunity)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-primary dark:text-white hover:bg-hover dark:hover:bg-border dark:bg-neutral-700/50 rounded-md transition-colors"
                      >
                        <Pencil className="w-4 h-4 text-primary dark:text-blue-400" />
                        <span>{t("common.edit")}</span>
                      </button>
                      <div className="h-px bg-border dark:bg-neutral-700/50 my-1" />
                      <button
                        onClick={() => handleRemoveOpportunity(opportunity.id)}
                        disabled={opportunityManagement.deleting}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>{opportunityManagement.deleting ? 'Removendo...' : 'Remover'}</span>
                      </button>
                    </PopoverContent>
                  </Popover>

                  {/* Badge de Status Ativo */}
                  <div className="mt-2 flex items-center gap-1.5 pl-4">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-[10px] text-green-400 font-medium">{t("panel.activeInCrm")}</span>
                  </div>
                </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleNewOpportunity}
                    className="w-full text-xs"
                  >
                    {t("panel.newOpportunity")}
                  </Button>
                </>
              ) : (
                <>
                  {duplicatesInSelectedPipeline.length > 0 && (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                      <p className="font-semibold mb-1">{tCrm("duplicateWarning.title")}</p>
                      {tCrm("duplicateWarning.body", {
                        count: duplicatesInSelectedPipeline.length,
                        titles: duplicatesInSelectedPipeline.map(opp => opp.title || opp.stage_name).join(", "),
                      })}
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium">{t("panel.funnel")}</p>
                    <Select
                      value={selectedPipelineId}
                      onValueChange={handlePipelineChange}
                      disabled={loadingPipelines}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t("panel.pickFunnel")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {pipelines.map((pipeline) => (
                            <SelectItem key={pipeline.id} value={pipeline.id}>
                              {pipeline.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedPipelineId && (
                    <div className="flex flex-col gap-2">
                      <p className="text-sm font-medium">{t("panel.stage")}</p>
                      <Select
                        value={selectedStageId}
                        onValueChange={handleStageChange}
                        disabled={loadingPipelines || !selectedPipelineId}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t("panel.pickStage")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {filteredStages.map((stage) => (
                              <SelectItem key={stage.id} value={stage.id}>
                                {stage.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {selectedPipelineId && selectedStageId && (
                    <Button
                      onClick={handleSaveOpportunity}
                      disabled={opportunityManagement.creating || opportunityManagement.updating}
                      size="sm"
                      className="w-full bg-primary hover:bg-primary-hover text-primary-foreground"
                    >
                      {opportunityManagement.creating || opportunityManagement.updating ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t("panel.savingToCrm")}
                        </>
                      ) : (
                        t("panel.saveToCrm")
                      )}
                    </Button>
                  )}

                  {opportunities.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEdit}
                      className="text-xs"
                    >
                      {t("common.cancel")}
                    </Button>
                  )}
                </>
              )}

              {showSaveSuccess && (
                <div className="text-xs text-green-400 bg-green-500/10 p-3 rounded-lg flex items-center gap-2 border border-green-500/30 animate-in fade-in slide-in-from-top-2 duration-300">
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">{t("panel.saved")}</span>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion type="single" collapsible defaultValue="item-scheduled">
        <AccordionItem value="item-scheduled">
          <AccordionTrigger>{t("panel.scheduled")}</AccordionTrigger>
          <AccordionContent>
            <LeadScheduledMessages contactId={selectedContact.id} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion type="single" collapsible defaultValue="item-sequences">
        <AccordionItem value="item-sequences">
          <AccordionTrigger>{t("panel.sequences")}</AccordionTrigger>
          <AccordionContent>
            <LeadFollowUpSequences contactId={selectedContact.id} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion type="single" collapsible defaultValue="item-4">
        <AccordionItem value="item-4">
          <AccordionTrigger>{t("common.tags")}</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant="default2"
                  className="bg-[#EEF4FF] hover:bg-[#DBEAFE] dark:bg-neutral-800 dark:hover:bg-hover dark:hover:bg-neutral-700 flex items-center cursor-pointer px-2 py-1 transition-colors"
                  onClick={() => setShowTagModal(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  <span>{t("common.add")}</span>
                </Badge>

                {contactTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="tag"
                    className="capitalize flex items-center gap-1 pr-1 group cursor-pointer hover:opacity-90 transition-colors"
                  >
                    <span>{tag}</span>
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      disabled={contactManagement.updating}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>

              {contactTags.length === 0 && (
                <p className="text-xs text-muted-foreground dark:text-neutral-500">{t("panel.noTags")}</p>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <Accordion type="single" collapsible defaultValue="item-5">
        <AccordionItem value="item-5">
          <AccordionTrigger>{t("panel.activeCampaigns")}</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-3">
              {loadingCampaignHistory ? (
                <p className="text-xs text-muted-foreground dark:text-neutral-500">{t("panel.loadingCampaigns")}</p>
              ) : uniqueCampaignNames.length > 0 ? (
                <div className="max-h-[200px] overflow-y-auto flex flex-wrap gap-2 pr-1">
                  {uniqueCampaignNames.map((name) => (
                    <Badge
                      key={name}
                      variant="tag"
                      className="capitalize"
                    >
                      {name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-muted-foreground dark:text-neutral-500">
                    {t("panel.noCampaigns")}
                  </p>
                  <p className="text-xs text-muted-foreground/80 dark:text-neutral-600">
                    {t("panel.campaignsHint")}
                  </p>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <TagSelectorModal
        open={showTagModal}
        onOpenChange={setShowTagModal}
        availableTags={availableTags}
        selectedTags={contactTags}
        onApplyTags={handleApplyTags}
        onTagCreated={handleTagCreated}
      />

      <WonOpportunityDialog
        open={wonDialogOpen}
        onClose={handleCancelWon}
        onConfirm={handleConfirmWon}
        opportunityTitle={selectedContact ? getDisplayName(selectedContact) : ''}
      />

      <LossReasonDialog
        open={lossReasonDialogOpen}
        onClose={handleCancelLoss}
        onConfirm={handleConfirmLoss}
        opportunityTitle={selectedContact ? getDisplayName(selectedContact) : ''}
      />
    </ScrollArea>
  );
};

/**
 * Memoizado na exportação default (a que ChatInterface usa): este painel monta 8 hooks de
 * dados e ~20 estados, e não tem relação nenhuma com o que está sendo digitado ao lado.
 */
export default memo(LeadManagementPanel);
